import { useEffect, useState } from 'react';
import { railsFetch } from '../../utils';
import { IconCircleCheck, IconPlayerPlay, IconExternalLink, IconListCheck, IconToggleRight, IconX } from '@tabler/icons';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Alert } from '../Notifications/Alert';
import IconRosetteDiscountCheck from '../../assets/svg/IconRosetteDiscountCheck';

const videoUrl = 'https://www.youtube.com/embed/-Lz_Bme8MHM?si=6tJS3NWoMehKSseX';

const AppEmbedOnboarding = ({ handleClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Completed, setStep1Completed] = useState(false);
  const [step2Confirmed, setStep2Confirmed] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');
  const [appEmbedUrl, setAppEmbedUrl] = useState('https://shopify.com/');
  const [isLoadingUrl, setIsLoadingUrl] = useState(true);
  const [urlError, setUrlError] = useState(null);

  useEffect(() => {
    const fetchURL = async () => {
      setUrlError('');
      setIsLoadingUrl(true);
      try {
        const res = await railsFetch(`//${window.appDomain}/shopify/get_theme_app_embed_url?site_id=${window.siteId}&token=${window.token}`);
        const data = await res.json();
        if(data && data.embed_url){
          setAppEmbedUrl(data.embed_url);
        } else if (data && data.error) {
          setUrlError(data.error);
        }
      } catch (error) {
        setUrlError('Failed to fetch app embed URL. Please reload the page.');
      } finally {
        setIsLoadingUrl(false);
      }
    };

    fetchURL();
  }, []);

  // Handle opening app embed URL
  const handleOpenAppEmbed = () => {
    if (appEmbedUrl) {
      window.open(appEmbedUrl, '_blank', 'noopener,noreferrer');
      setStep1Completed(true);
      setCurrentStep(2);
    }
  };

  // Handle verification
  const handleVerify = async () => {
    if (!step2Confirmed) {
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);
    setErrorMessage('');

    try {
      // Call the backend verification API
      const result = await railsFetch(`//${window.appDomain}/sites/${window.siteId}/check_theme_app_embed_enabled?token=${window.token}`).then(res => res.json()).catch(() => null);
      
      if (result && result.enabled) {
        setVerificationResult('success');
      } else {
        setVerificationResult('error');
        setErrorMessage('Failed to verify app embed. Please make sure you have enabled the app embed in your theme settings.');
      }
    } catch (error) {
      setVerificationResult('error');
      setErrorMessage(error?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle try again
  const handleTryAgain = () => {
    setCurrentStep(1);
    setStep1Completed(false);
    setStep2Confirmed(false);
    setVerificationResult(null);
    setErrorMessage('');
  };

  const handleFinish = () => {
    handleClose();
  };

  // Render step indicator
  const renderStepIndicator = (stepNumber, isCompleted, isActive) => {
    if (isCompleted || verificationResult === 'success') {
      return (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-success flex-shrink-0">
          <IconCircleCheck size={20} color="#FFFFFF" stroke={2} />
        </div>
      );
    }
    
    return (
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
        isActive 
          ? 'bg-primary text-white shadow-[0_0_0_4px_rgba(0,87,255,0.1)]' 
          : 'bg-gray-600 text-gray-800'
      }`}>
        {/* <span className="text-sm font-semibold">{stepNumber}</span> */}
        { stepNumber === 1 ? <IconToggleRight size={20} /> : <IconRosetteDiscountCheck strokeColor='white' size={20} color='transparent' stroke={1} />}
      </div>
    );
  };

  return (
    <div className="max-w-[600px] h-screen overflow-auto mx-auto px-6 py-10">
      <div className='absolute top-4 right-4 cursor-pointer text-gray-600 hover:text-gray-800' onClick={handleClose}>
        <IconX />
      </div>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className='bg-primary p-2 rounded'>
            <IconListCheck size={24} color="white" stroke={2} />
          </div>
        </div>
        <h2 className="text-2xl font-semibold mb-2">Just One Step Away</h2>
        <p className="text-sm text-secondary">
          Enable Mailmunch in your theme to activate embedded forms.
        </p>
      </div>

      {/* Video Section */}
      <div className="mb-8 rounded-xl overflow-hidden bg-blue-400 relative" style={{ aspectRatio: '16 / 9' }}>
        {videoUrl ? (
          <iframe
            className="w-full h-full block"
            src={videoUrl}
            title="App Embed Guide"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <IconPlayerPlay size={48} className="mb-3" />
            <p>Video guide coming soon</p>
          </div>
        )}
      </div>

      {/* Steps Section */}
      <div className="bg-blue-100 rounded-xl p-6">
        {/* Step 1: Enable the Mailmunch theme app embed */}
        <div className={`flex gap-4 relative transition-opacity mb-4`}>
          <div className="flex flex-col items-center relative">
            {renderStepIndicator(1, step1Completed, currentStep === 1)}
            <div className="w-0.5 h-16 bg-primary mt-2" />
          </div>
          
          <div className="flex-1 pt-1">
            <div className="flex justify-between items-start">
              <h3 className="text-base">Enable the Mailmunch theme app embed</h3>
            </div>
            <p className="text-sm text-gray-900 leading-relaxed">
              Navigate to Shopify admin, install Mailmunch, and save changes. We'll help you there automatically.
            </p>
            {appEmbedUrl && !step1Completed && !isLoadingUrl && !urlError && (
              <button 
                onClick={handleOpenAppEmbed}
                disabled={verificationResult === 'success'}
                className={`text-lg text-primary flex items-center gap-1 mt-2 hover:text-primary-dark transition-colors ${
                  verificationResult === 'success' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                Open <IconExternalLink size={14} />
              </button>
            )}
            {isLoadingUrl && (
              <p className="text-sm text-gray-800 mt-2">Loading...</p>
            )}
            {urlError && (
              <Alert
                show={true}
                information={urlError}
                color="danger"
                icon="alert"
                showClose={false}
                textStyles="text-sm"
              />
            )}
          </div>
        </div>

        {/* Step 2: Verify Settings */}
        <div className={`flex gap-4 relative transition-opacity ${!step1Completed ? 'pointer-events-none' : ''}`}>
          <div className="flex flex-col items-center">
            {renderStepIndicator(2, verificationResult === 'success', currentStep === 2)}
          </div>
          
          <div className="flex-1 pt-1">
            <h3 className="text-base ">Verify Settings</h3>
            <p className="text-sm text-gray-900 leading-relaxed mb-2">
              Verify that all settings are correct and your forms are live.
            </p>
            
            {step1Completed && verificationResult !== 'success' && (
              <div className="flex flex-col gap-3 ">
                <Checkbox 
                  checked={step2Confirmed}
                  onClick={() => setStep2Confirmed(!step2Confirmed)}
                  disabled={isVerifying}
                  label="I have completed the steps above"
                  className="text-sm"
                />
                
                <Button
                  action="primary"
                  onClick={handleVerify}
                  disabled={!step2Confirmed || isVerifying}
                  label={isVerifying ? (
                    <div className="flex items-center gap-2">
                      <span>Verifying...</span>
                    </div>
                  ) : 'Verify'}
                  className="self-start"
                />
              </div>
            )}
          </div>
        </div>

      </div>
        {/* Success Alert */}
        {verificationResult === 'success' && (
          <div className="flex flex-col gap-4 mt-6">
            <Alert
              show={true}
              information="Your app embed has been successfully enabled. You can now use embedded forms in your Shopify store."
              color="success"
              icon="check"
              showClose={false}
              textStyles="text-sm"
            />
            <Button
              action="primary"
              onClick={() => handleFinish()}
              label="Finish Setup"
              className="self-center w-full justify-center px-4 py-2"
            />
          </div>
        )}

        {/* Error Alert */}
        {verificationResult === 'error' && (
          <div className="flex flex-col gap-4 mt-6">
            <Alert
              show={true}
              information={errorMessage}
              color="danger"
              icon="alert"
              showClose={false}
              textStyles="text-sm"
            />
            <Button
              action="danger"
              onClick={handleTryAgain}
              label="Try Again"
              className="self-center w-full justify-center"
            />
          </div>
        )}
    </div>
  );
};

export { AppEmbedOnboarding };
